import { Head, Link, router } from "@inertiajs/react";
import { ArrowLeft, Check, Copy, LogIn, UserCheck } from "lucide-react";
import { useState } from "react";
import BizminerAdminLayout from "@/bizminer/layouts/admin/BizminerAdminLayout";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  approve_bizminer_admin_organization_user_path,
  bizminer_admin_organization_users_path,
  generate_magic_link_bizminer_admin_organization_user_path,
  update_role_bizminer_admin_organization_user_path,
} from "@/routes";
import type {
  BizminerAdminOrganization,
  BizminerUser,
} from "@/types/serializers";
import { InviteDialog } from "./_components/InviteDialog";

interface Props {
  organization: BizminerAdminOrganization;
  user: BizminerUser;
}

const ROLES = [
  { value: "pending", label: "Pending", description: "Awaiting approval" },
  { value: "viewer", label: "Viewer", description: "View-only access" },
  { value: "member", label: "Member", description: "Standard access" },
  { value: "admin", label: "Admin", description: "Full access" },
];

const EXPIRATION_OPTIONS = [
  { value: 1, label: "1 hour" },
  { value: 24, label: "1 day" },
  { value: 168, label: "7 days" },
  { value: 336, label: "14 days" },
  { value: 720, label: "30 days" },
];

const ROLE_CONFIG: Record<string, { bg: string; text: string; label: string }> =
  {
    pending: {
      bg: "bg-yellow-500/10",
      text: "text-yellow-600",
      label: "Pending",
    },
    viewer: { bg: "bg-blue-500/10", text: "text-blue-600", label: "Viewer" },
    member: { bg: "bg-green-500/10", text: "text-green-600", label: "Member" },
    admin: { bg: "bg-purple-500/10", text: "text-purple-600", label: "Admin" },
  };

function RoleBadge({ role }: { role: string }) {
  const { bg, text, label } = ROLE_CONFIG[role] || ROLE_CONFIG.member;
  return (
    <span
      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${bg} ${text}`}
    >
      {label}
    </span>
  );
}

function PageHeader({
  organization,
  user,
}: {
  organization: BizminerAdminOrganization;
  user: BizminerUser;
}) {
  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
      <div className="flex justify-between items-center py-6">
        <div className="flex items-center space-x-4">
          <Link href={bizminer_admin_organization_users_path(organization.id)}>
            <Button variant="ghost" size="sm">
              <ArrowLeft className="h-4 w-4 mr-2" />
              Back to Users
            </Button>
          </Link>
          <h1 className="text-2xl font-bold text-foreground">{user.name}</h1>
          <RoleBadge role={user.role} />
        </div>
      </div>
    </div>
  );
}

function InfoItem({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className="text-foreground">{value}</p>
    </div>
  );
}

function UserInfoCard({ user }: { user: BizminerUser }) {
  return (
    <Card>
      <CardHeader>
        <CardTitle>User Information</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <InfoItem label="Name" value={user.name} />
        <InfoItem label="Email" value={user.email_address} />
        <InfoItem label="Last Session" value={user.last_session || "Never"} />
        <InfoItem label="Member Since" value={user.created_at} />
      </CardContent>
    </Card>
  );
}

function RoleManagementCard({
  organization,
  user,
}: {
  organization: BizminerAdminOrganization;
  user: BizminerUser;
}) {
  const [approving, setApproving] = useState(false);
  const [updatingRole, setUpdatingRole] = useState(false);

  const approveUser = () => {
    setApproving(true);
    router.post(
      approve_bizminer_admin_organization_user_path(organization.id, user.id),
      {},
      {
        preserveScroll: true,
        onFinish: () => setApproving(false),
      },
    );
  };

  const updateRole = (newRole: string) => {
    if (newRole === user.role) return;

    setUpdatingRole(true);
    router.patch(
      update_role_bizminer_admin_organization_user_path(
        organization.id,
        user.id,
      ),
      { role: newRole },
      {
        preserveScroll: true,
        onFinish: () => setUpdatingRole(false),
      },
    );
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Role Management</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm font-medium text-muted-foreground mb-2">
            User Role
          </p>
          <Select
            value={user.role}
            onValueChange={updateRole}
            disabled={updatingRole}
          >
            <SelectTrigger className="w-full">
              <SelectValue>
                {ROLES.find((r) => r.value === user.role)?.label}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              {ROLES.map((role) => (
                <SelectItem key={role.value} value={role.value}>
                  <div className="flex flex-col">
                    <span>{role.label}</span>
                    <span className="text-xs text-muted-foreground">
                      {role.description}
                    </span>
                  </div>
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {user.role === "pending" && (
          <Button
            onClick={approveUser}
            disabled={approving}
            className="w-full"
            variant="outline"
          >
            <UserCheck className="h-4 w-4 mr-2" />
            {approving ? "Approving..." : "Approve User"}
          </Button>
        )}
      </CardContent>
    </Card>
  );
}

function MagicLinkGenerator({
  onGenerate,
}: {
  onGenerate: (hours: number) => Promise<string>;
}) {
  const [expiresIn, setExpiresIn] = useState(24);
  const [copying, setCopying] = useState(false);
  const [copied, setCopied] = useState(false);

  const copyMagicLink = async () => {
    setCopying(true);
    try {
      const url = await onGenerate(expiresIn);
      await navigator.clipboard.writeText(url);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } finally {
      setCopying(false);
    }
  };

  return (
    <div className="flex items-center gap-2">
      <Select
        value={expiresIn.toString()}
        onValueChange={(v) => setExpiresIn(Number(v))}
      >
        <SelectTrigger className="w-[110px]">
          <SelectValue>
            {EXPIRATION_OPTIONS.find((o) => o.value === expiresIn)?.label}
          </SelectValue>
        </SelectTrigger>
        <SelectContent>
          {EXPIRATION_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value.toString()}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <Button
        onClick={copyMagicLink}
        className="flex-1"
        variant="outline"
        disabled={copying}
      >
        {copied ? (
          <>
            <Check className="h-4 w-4 mr-2 text-green-600" />
            Copied!
          </>
        ) : (
          <>
            <Copy className="h-4 w-4 mr-2" />
            {copying ? "Generating..." : "Copy Magic Link"}
          </>
        )}
      </Button>
    </div>
  );
}

function QuickAccessCard({
  organization,
  user,
}: {
  organization: BizminerAdminOrganization;
  user: BizminerUser;
}) {
  const [loggingIn, setLoggingIn] = useState(false);

  const generateMagicLink = async (hours: number): Promise<string> => {
    const csrfToken = document
      .querySelector('meta[name="csrf-token"]')
      ?.getAttribute("content");

    const response = await fetch(
      generate_magic_link_bizminer_admin_organization_user_path(
        organization.id,
        user.id,
      ),
      {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "X-CSRF-Token": csrfToken || "",
        },
        body: JSON.stringify({ expires_in: hours }),
      },
    );
    const { magic_url } = await response.json();
    return magic_url;
  };

  const loginAsUser = async () => {
    setLoggingIn(true);
    try {
      const url = await generateMagicLink(1);
      window.open(url, "_blank", "noopener,noreferrer");
    } finally {
      setLoggingIn(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>Quick Access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <Button
          onClick={loginAsUser}
          className="w-full"
          variant="default"
          disabled={loggingIn}
        >
          <LogIn className="h-4 w-4 mr-2" />
          {loggingIn ? "Opening..." : "Login as User"}
        </Button>

        <MagicLinkGenerator onGenerate={generateMagicLink} />

        <InviteDialog organization={organization} user={user} />
      </CardContent>
    </Card>
  );
}

export default function Show({ organization, user }: Props) {
  return (
    <BizminerAdminLayout>
      <Head
        title={`${user.name} - Users - ${organization.name} - Super Admin`}
      />

      <div className="min-h-screen">
        <PageHeader organization={organization} user={user} />

        <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
          <div className="px-4 py-6 sm:px-0">
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              <UserInfoCard user={user} />

              <div className="space-y-6">
                <RoleManagementCard organization={organization} user={user} />
                <QuickAccessCard organization={organization} user={user} />
              </div>
            </div>
          </div>
        </div>
      </div>
    </BizminerAdminLayout>
  );
}
